import { ReactComponent as AiIcon } from 'assets/icons/ai.svg';
import { ReactComponent as DatabaseIcon } from 'assets/icons/database.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as PlayIcon } from 'assets/icons/play.svg';
import { ReactComponent as ScenarioIcon } from 'assets/icons/scenario.svg';
import { ReactComponent as ToolIcon } from 'assets/icons/settings.svg';
import { ReactComponent as SupplierRegulationIcon } from 'assets/icons/supplierRegulation.svg';

import type { ComponentType, SVGProps } from 'react';
import type { TAiChatToolName } from '../interfaces/IAiChatTools';

export type TChatStepIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Shown for a tool with no icon of its own. */
export const DEFAULT_TOOL_ICON: TChatStepIconComponent = ToolIcon;

/**
 * Icons for the tools worth telling apart at a glance, keyed by tool name.
 *
 * Giving one tool an icon of its own is one entry here and nothing else — the step row never
 * branches on the tool, the same way `TOOL_COMPONENTS` works for the body of a call.
 *
 * Keyed by the tools the FE knows, and open to any other name, the same way `TOOL_COMPONENTS` is.
 *
 * Built without a prototype: the key is a tool name the API chose, and on a plain object a tool
 * called `constructor` would look up a function that React cannot render.
 *
 * @see [12.2 Show the tool on the frontend](../../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
const TOOL_ICONS: Record<TAiChatToolName | (string & {}), TChatStepIconComponent> = Object.create(null);

// A lookup reads the stored documents and a write adds one: an eye, and a pencil.
TOOL_ICONS.lookUpDocument = EyeIcon;
TOOL_ICONS.writeDocument = PencilIcon;
TOOL_ICONS.lookupStandards = EyeIcon;
TOOL_ICONS.lookupPublicCompany = EyeIcon;
TOOL_ICONS.lookupDossierPublicFacts = EyeIcon;
TOOL_ICONS.searchWatchCompanies = EyeIcon;
TOOL_ICONS.createWatchCompanies = PencilIcon;

// A data fetch reads the database, and a created datasource writes one. The display tools,
// `showDatasource` and `showChart`, are no steps and have no icon here: only their failure is a
// step, and `AiChatDisplayFailed` draws that icon itself.
TOOL_ICONS.fetchData = DatabaseIcon;
TOOL_ICONS.createDatasource = PencilIcon;

// An impact analysis is made and changed as a scenario, and started as a run.
TOOL_ICONS.createImpactAnalysis = ScenarioIcon;
TOOL_ICONS.updateImpactAnalysis = ScenarioIcon;
TOOL_ICONS.startImpactAnalysis = PlayIcon;

// An agent session is made and changed with the AI agent of the map's tool panel, and its search
// started as a run.
TOOL_ICONS.createAgentSession = AiIcon;
TOOL_ICONS.updateAgentSessionUnits = AiIcon;
TOOL_ICONS.startAgentSearch = PlayIcon;

// A monitoring session is made and changed with the regulation agent's icon, whatever its agent:
// the icon is keyed by tool, and the row header names the agent. It is started as a run.
TOOL_ICONS.createMonitoringSession = SupplierRegulationIcon;
TOOL_ICONS.updateMonitoringSessionUnits = SupplierRegulationIcon;
TOOL_ICONS.startMonitoringSession = PlayIcon;

export default TOOL_ICONS;
